/**
 * THE DOG POOP DILEMMA — Dynamic Dialog & Gemini AI Scenario Engine
 * Quản lý kho thoại phong phú, sinh kịch bản thực tế qua Gemini 3.8 Flash, lưu LocalStorage cache
 */

export type DialogCategory =
  | 'teastall_idle'
  | 'teastall_clean'
  | 'altruist_witness_defect'
  | 'altruist_praise_cooperate'
  | 'defector_mock_cooperate'
  | 'defector_flee'
  | 'titfortat_witness_defect'
  | 'titfortat_revenge'
  | 'titfortat_peace'
  | 'chicken_game'
  | 'inspector_alert';

/**
 * Tính toán thời gian hiển thị vừa vặn để người chơi kịp đọc trọn vẹn câu thoại
 * Tốc độ đọc tiếng Việt trung bình: ~65ms/ký tự + 1.2s độ trễ nhận diện thị giác
 */
export function calcReadingDuration(text: string): number {
  if (!text) return 2500;
  const ms = 1200 + text.length * 65;
  return Math.max(2400, Math.min(5500, ms));
}

// Kho thoại dự phòng giàu sắc thái văn hóa vỉa hè Hà Nội / ngõ phố
const FALLBACK_DIALOGS: Record<DialogCategory, string[]> = {
  teastall_idle: [
    'Gớm, dắt chó đi đâu mà mắt lấm lét thế cháu?',
    'Chó đẹp đấy, nhưng xả bậy là cả xóm chụp ảnh ngay nhé!',
    'Hút điếu thuốc lào nhìn hai đứa dắt chó đi dạo cũng vui.',
    'Này cậu gì ơi, ngõ này camera giăng kín đấy, liệu mà giữ gìn!',
    'Dắt chó đi dạo hay đi tìm chỗ xả đấy?',
  ],
  teastall_clean: [
    'Gớm, dắt chó đi ỉa mà cũng bày đặt mang túi, hôi rình cả ngõ!',
    'Dọn sạch đấy, tí ghé quán cô làm cốc trà đá cô giảm cho nghìn rưởi!',
    'Lần đầu thấy có đứa chịu cúi lưng hốt phân trong cái ngõ này.',
    'Hốt xong nhớ vứt đúng thùng rác nhé, vứt cổng nhà cô là chết đòn!',
  ],
  altruist_witness_defect: [
    'Ối dồi ôi, thanh niên mặt mũi sáng sủa mà tính chuồn hả con vợ!',
    'Chụp nét căng rồi nhé! Chuẩn bị tinh thần lên Group Cư Dân!',
    'Đứng lại ngay! Văn minh lịch sự để đâu mà để chó ỉa xong chuồn?',
    'Tôi đã quay lại video 4K rồi, tự giác quay lại dọn ngay!',
  ],
  altruist_praise_cooperate: [
    'Cũng biết điều đấy, tặng cậu 2 điểm công dân gương mẫu!',
    'Nuôi chó có ý thức như này có phải xóm làng yên vui không!',
    'Tốt lắm bạn ơi! Xã hội cần nhiều người biết cúi xuống hốt phân như bạn!',
    'Đáng khen! Chó ngoan chủ cũng biết điều!',
  ],
  defector_mock_cooperate: [
    'Đần thế con vợ, dọn làm gì cho bẩn tay!',
    'Khổ thân, làm nô tì dọn phân cho chó giữa đường thế kia!',
    'Ơ kìa, cứ chuồn như tao có phải khỏe re không, ai bắt được!',
    'Bày đặt làm người tử tế giữa cái ngõ này à!',
  ],
  defector_flee: [
    'Chuồn lẹ không bà tổ trưởng ra bây giờ!',
    'Chó xả xong rồi, vắt chân lên cổ mà chạy thôi!',
    'Ai ỉa đâu không biết, tao vô tội nhé!',
    'Phóng nhanh kẻo camera quét trúng!',
  ],
  titfortat_witness_defect: [
    'Tao ghim mày rồi đấy, đợi đấy con vợ!',
    'Dám chuồn trước mặt tao à? Đợi chó tao mót ruột nhé!',
    'Mày ỉa bậy được thì tao cũng biết đường sang nhà mày!',
    'Ghi nhớ biển số mặt mày rồi, quả báo sắp tới!',
  ],
  titfortat_revenge: [
    'Ăn miếng trả miếng nhé con vợ!',
    'Một bãi trả một bãi, sòng phẳng cả ngõ!',
    'Hôm trước mày chuồn, hôm nay tao trả đúng trước cửa nhà mày!',
    'Nếm mùi mìn vỉa hè đi bạn hiền!',
  ],
  titfortat_peace: [
    'Người văn minh, duyệt!',
    'Chào đồng chí yêu môi trường, giữ hòa khí nhé!',
    'Sống biết điều cho đời thanh thản.',
    'Mày dọn thì tao cũng dọn, hòa bình là trên hết!',
  ],
  chicken_game: [
    'Nhìn cái gì? Chó tao không ngán chó mày đâu!',
    'Gầm gừ cái gì, đứa nào giật xích trước đứa đấy hèn!',
    'Căng thẳng đấy, nhịn ỉa thi không con vợ!',
    'Đứng lại đọ độ lì hay sợ chó sủa cả xóm dậy?',
  ],
  inspector_alert: [
    'Chó nhà ai sủa inh ỏi ngoài kia đấy?',
    'Đề nghị bà con dắt chó có rọ mõm và không xả bậy ra ngõ!',
    'Ai dắt chó vừa làm động tác khả nghi đấy hả?',
    'Camera số 3 vừa báo động, tôi ra kiểm tra ngay!',
  ],
};

const CACHE_KEY = 'dogpoop_dialog_cache_v2';

export class DialogService {
  private static instance: DialogService;
  private cache: Record<DialogCategory, string[]>;
  private isGenerating = new Map<DialogCategory, boolean>();
  private apiKey: string;

  private constructor() {
    const metaEnv = (import.meta as unknown as { env?: Record<string, string> }).env;
    this.apiKey =
      metaEnv?.VITE_GEMINI_API_KEY ||
      'AQ.Ab8RN6J7DefWeZwo2plcX50lfowUF5584uhU_xznmOmgcOQSpg';

    this.cache = this.loadCache();
    // Khởi động prefetch nền một số danh mục thường dùng
    this.prefetchInBackground('teastall_idle');
    this.prefetchInBackground('chicken_game');
    this.prefetchInBackground('altruist_witness_defect');
  }

  public static getInstance(): DialogService {
    if (!DialogService.instance) {
      DialogService.instance = new DialogService();
    }
    return DialogService.instance;
  }

  /**
   * Lấy một câu thoại ngẫu nhiên theo danh mục, có xoay vòng và tự động refill bằng Gemini
   */
  public getDialog(category: DialogCategory): string {
    const list = this.cache[category] && this.cache[category].length > 0
      ? this.cache[category]
      : FALLBACK_DIALOGS[category];

    const idx = Math.floor(Math.random() * list.length);
    const chosen = list[idx] || FALLBACK_DIALOGS[category][0]!;

    // Nếu kho thoại sắp cạn hoặc được gọi, kích hoạt sinh thêm trong nền
    if (list.length < 8) {
      this.fetchFromGemini(category);
    }

    return chosen;
  }

  /**
   * Gọi Gemini 3.8 Flash trong nền để sinh 4-6 câu thoại hài hước, thực chiến ngõ phố
   */
  public async fetchFromGemini(category: DialogCategory): Promise<void> {
    if (this.isGenerating.get(category) || !this.apiKey) return;
    this.isGenerating.set(category, true);

    const prompts: Record<DialogCategory, string> = {
      teastall_idle: 'Bà bán trà đá ngõ phố Hà Nội cà khịa người dắt chó đi ngang.',
      teastall_clean: 'Bà bán trà đá bĩu môi cà khịa khi thấy thanh niên cúi xuống hốt phân chó bỏ túi.',
      altruist_witness_defect: 'NPC Thánh Mẫu yêu môi trường bắt quả tang thanh niên dắt chó ỉa bậy tính chuồn lẹ.',
      altruist_praise_cooperate: 'NPC Thánh Mẫu khen ngợi ban ơn kiểu thảo mai khi thấy người khác dọn phân chó.',
      defector_mock_cooperate: 'Thanh niên chuyên ỉa bậy chạy trốn cười khẩy chê bai người khác dọn phân là đần.',
      defector_flee: 'Thanh niên vừa cho chó ỉa bậy vắt chân lên cổ chạy tháo thân khỏi camera.',
      titfortat_witness_defect: 'NPC thù dai ghim mặt người chơi vì dám chuồn không dọn phân.',
      titfortat_revenge: 'NPC trả thù xả đúng một bãi phân trước cửa nhà người chơi.',
      titfortat_peace: 'NPC hòa nhã khen người chơi biết điều giữ hòa khí xóm ngõ.',
      chicken_game: 'Hai chủ chó đối đầu ở ngõ hẹp, chó giằng xích gầm gừ kích thích mót ruột.',
      inspector_alert: 'Bác tổ trưởng dân phố nghe tiếng chó sủa quát ra ngõ kiểm tra.',
    };

    const scenario = prompts[category] || 'Hội thoại dắt chó vỉa hè ngõ phố.';
    const systemInstruction = `Bạn là biên kịch game "The Dog Poop Dilemma (Spatial Game Theory Vỉa Hè Hà Nội)".
Hãy sinh ra đúng 4 câu thoại ngắn (mỗi câu dưới 14 từ), cực kỳ hài hước, trần trụi, mang đậm văn hóa ngõ phố Hà Nội cho tình huống: "${scenario}".
Yêu cầu:
- Mỗi câu trên 1 dòng riêng biệt.
- Không đánh số đầu dòng, không gạch đầu dòng, không để dấu ngoặc kép.
- Từ ngữ tự nhiên, dí dỏm, thực chiến.`;

    try {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent?key=${this.apiKey}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: systemInstruction }] }],
          generationConfig: {
            temperature: 0.85,
            maxOutputTokens: 250,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Gemini API HTTP ${response.status}`);
      }

      const data = await response.json();
      const rawText: string = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
      const lines = rawText
        .split('\n')
        .map((l: string) => l.replace(/^[\d\.\-\*\s"']+|["']+$/g, '').trim())
        .filter((l: string) => l.length > 5 && l.length < 80);

      if (lines.length > 0) {
        // Thêm vào danh mục, loại bỏ trùng lặp
        const existing = new Set(this.cache[category]);
        for (const line of lines) {
          existing.add(line);
        }
        this.cache[category] = Array.from(existing);
        this.saveCache();
      }
    } catch {
      // Offline hoặc lỗi mạng: giữ nguyên fallback mượt mà
    } finally {
      this.isGenerating.set(category, false);
    }
  }

  private prefetchInBackground(category: DialogCategory): void {
    setTimeout(() => {
      this.fetchFromGemini(category);
    }, 1500);
  }

  private loadCache(): Record<DialogCategory, string[]> {
    try {
      const saved = localStorage.getItem(CACHE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge với fallback để luôn có đầy đủ key
        const merged: any = {};
        for (const k of Object.keys(FALLBACK_DIALOGS) as DialogCategory[]) {
          merged[k] = Array.from(new Set([...(parsed[k] || []), ...FALLBACK_DIALOGS[k]]));
        }
        return merged;
      }
    } catch {
      // Ignore
    }

    // Default to fallback clone
    const initial: any = {};
    for (const k of Object.keys(FALLBACK_DIALOGS) as DialogCategory[]) {
      initial[k] = [...FALLBACK_DIALOGS[k]];
    }
    return initial;
  }

  private saveCache(): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));
    } catch {
      // Ignore storage errors
    }
  }
}
